import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import analytics from "@/lib/segment";
import { JsonLd, pricingFaqSchema, productWithOffersSchema } from "@/components/seo/JsonLd";
import { Helmet } from "react-helmet-async";
import { PricingHero } from "@/components/pricing/PricingHero";
import { PricingCard } from "@/components/pricing/PricingCard";
import { LaunchKitSection } from "@/components/pricing/LaunchKitSection";
import { TrustFooter } from "@/components/pricing/TrustFooter";

type PlanTier = "free" | "pro" | "scale" | "fleet";

const plansData = [
  {
    tier: "free" as PlanTier,
    name: "Free",
    tagline: "Get Oriented",
    price: "$0",
    priceSubtext: "/ month",
    bestFor: "New or early-stage hosts with 1 car, deciding whether Turo is a good fit.",
    topFeatures: [
      "Free guides for getting to your first listing",
      "Add and track your first vehicle",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View progress to profitability",
      "Track maintenance timeline",
      "Community support",
    ],
    ctaText: "Sign Up for Free",
    highlighted: false,
  },
  {
    tier: "pro" as PlanTier,
    name: "Pro",
    tagline: "Know What To Do Next",
    price: "$29",
    priceSubtext: "/ month",
    bestFor: "Hosts with 2–5 vehicles needing organized tracking for their business.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Priority email support",
    ],
    ctaText: "Sign Up for Pro",
    highlighted: true,
  },
  {
    tier: "scale" as PlanTier,
    name: "Scale",
    tagline: "Focus Where It Matters",
    price: "$79",
    priceSubtext: "/ month",
    bestFor: "Growing hosts managing 6-15 vehicles needing operational efficiency.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Surface risks in your fleet",
      "Insights on where to focus your attention",
      "Phone & email support",
    ],
    ctaText: "Sign Up for Scale",
    highlighted: false,
  },
  {
    tier: "fleet" as PlanTier,
    name: "Fleet",
    tagline: "Avoid Risk and Chaos",
    price: "$199",
    priceSubtext: "/ month",
    bestFor: "Professional operators, teams, or large fleets.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Surface risks in your fleet",
      "Insights on where to focus your attention",
      "Capital allocation insights",
      "Depreciation curve analysis",
      "Dedicated support specialist",
      "Included setup and training",
      "Priority phone support",
    ],
    ctaText: "Sign Up for Fleet",
    highlighted: false,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { subscriptionTier, isProOrAbove, upgradeSubscription } = useSubscription();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingTier, setLoadingTier] = useState<PlanTier | null>(null);

  // Check for checkout result in URL
  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (checkoutResult === "cancelled") {
      toast({
        title: "Checkout cancelled",
        description: "You can try again whenever you're ready.",
      });
    }
  }, [searchParams, toast]);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePlanSelect = async (tier: PlanTier) => {
    const priceMap: Record<PlanTier, number> = {
      free: 0,
      pro: 29,
      scale: 79,
      fleet: 199,
    };
    const price = priceMap[tier];

    analytics.track("Plan Selected", {
      plan_tier: tier,
      plan_price: price,
      billing_interval: "monthly",
      source: "pricing_page",
      is_authenticated: isAuthenticated,
    });

    if (tier === "free" || !isAuthenticated) {
      navigate(`/auth?mode=signup&plan=${tier}&billing=monthly`);
      return;
    }

    if (isProOrAbove && tier === subscriptionTier) {
      toast({ title: "You're already on this plan" });
      return;
    }

    setLoadingTier(tier);
    try {
      if (isProOrAbove) {
        // User already has a paid Stripe subscription — change plan directly
        analytics.track("Upgrade Started", {
          plan_tier: tier,
          plan_price: price,
          billing_interval: "monthly",
          source: "pricing_page",
          current_tier: subscriptionTier,
        });

        await upgradeSubscription(tier as any, "monthly");

        analytics.track("Plan Changed", {
          plan_tier: tier,
          plan_price: price,
          billing_interval: "monthly",
          source: "pricing_page",
          previous_tier: subscriptionTier,
        });

        navigate("/app/dashboard?upgrade=success");
      } else {
        // Free-tier user — needs Stripe Checkout to create a subscription
        const response = await supabase.functions.invoke("create-checkout", {
          body: {
            tier,
            billingInterval: "monthly",
            returnUrl: window.location.origin,
          },
        });

        if (response.error) {
          throw new Error(response.error.message || "Failed to create checkout session");
        }

        if (response.data?.error) {
          throw new Error(response.data.error);
        }

        if (response.data?.url) {
          analytics.track("Checkout Started", {
            plan_tier: tier,
            plan_price: price,
            billing_interval: "monthly",
            source: "pricing_page",
          });
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
      }
    } catch (error: any) {
      analytics.track("Error Occurred", {
        error_type: "checkout",
        error_message: error.message,
        plan_tier: tier,
      });
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <link rel="canonical" href="https://hostcaptain.co/pricing" />
      </Helmet>
      <JsonLd data={[productWithOffersSchema, pricingFaqSchema]} />
      <Navbar />

      <PricingHero />

      {/* Pricing Cards Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {plansData.map((plan) => (
              <PricingCard
                key={plan.tier}
                name={plan.name}
                tagline={plan.tagline}
                price={plan.price}
                priceSubtext={plan.priceSubtext}
                bestFor={plan.bestFor}
                topFeatures={plan.topFeatures}
                ctaText={plan.ctaText}
                onCtaClick={() => handlePlanSelect(plan.tier)}
                isLoading={loadingTier === plan.tier}
                highlighted={plan.highlighted}
              />
            ))}
          </div>
        </div>
      </section>

      {/* <LaunchKitSection /> */}

      <TrustFooter />
    </div>
  );
}
